import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { APP_TOAST_EVENT, type AppToastDetail } from '../lib/toast';
import { boxSizingStyles } from '../styles/lit-shared';

const TOAST_MS = 2800;

@customElement('app-toast')
export class AppToast extends LitElement {
  @state() private message = '';
  @state() private visible = false;
  private _hideTimer: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(APP_TOAST_EVENT, this._onToast);
  }

  disconnectedCallback() {
    window.removeEventListener(APP_TOAST_EVENT, this._onToast);
    this._clearTimer();
    super.disconnectedCallback();
  }

  private _clearTimer() {
    if (this._hideTimer !== null) {
      window.clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }

  private _onToast = (event: Event) => {
    const message = (event as CustomEvent<AppToastDetail>).detail?.message?.trim();
    if (!message) return;

    this.message = message;
    this.visible = true;
    this._clearTimer();
    this._hideTimer = window.setTimeout(() => {
      this.visible = false;
    }, TOAST_MS);
  };

  static styles = [
    boxSizingStyles,
    css`
      :host {
        position: fixed;
        inset-inline: 0;
        bottom: var(--space-lg);
        z-index: 240;
        display: flex;
        justify-content: center;
        padding: 0 var(--space-md);
        pointer-events: none;
      }

      .toast {
        max-width: min(28rem, 100%);
        padding: 0.75rem 1.1rem;
        border-radius: var(--radius-sm);
        background: var(--color-bg-elevated);
        border: 1px solid var(--color-border-glow);
        box-shadow: var(--shadow-lg);
        color: var(--color-text);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.45;
        opacity: 0;
        visibility: hidden;
        transform: translateY(10px);
        transition:
          opacity var(--duration-normal) var(--ease-out),
          transform var(--duration-normal) var(--ease-out),
          visibility var(--duration-normal) var(--ease-out);
      }

      .toast--visible {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      @media (prefers-reduced-motion: reduce) {
        .toast {
          transition: opacity var(--duration-fast) var(--ease-out);
          transform: none;
        }
      }
    `,
  ];

  render() {
    return html`
      <div
        class="toast ${this.visible ? 'toast--visible' : ''}"
        role="status"
        aria-live="polite"
        aria-hidden=${this.visible ? 'false' : 'true'}
      >
        ${this.message}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-toast': AppToast;
  }
}
